import {
    type Flow,
    type AsyncFlow,
    type AsyncFlowState,
    type FlowSubscription,
    createFlow,
    createAsyncFlow,
} from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';
import { createDataSource } from '@adv-frontend/reactions';
import { UnexpectedError } from '@adv-frontend/error';
import { stableSerialize } from '@direct-frontend/stdlib';
import type { IterableDataSource } from '@adv-frontend/reactions';
import type {
    ApiEndpointQuery,
    InternalSerializeQueryArgs,
    QueryActionCreatorResult,
    QueryArgFrom,
    QueryResultSelectorResult,
    ResultTypeFrom,
    TagDescription,
} from '../rtk-query-types';
import type { GetStoreFn } from '../types';
import { createListenersCollection } from '../createListenersCollection';
import type { AnyApi } from '../buildCreateApi';
import type {
    AdditionalQueryState,
    AnyEndpointDefinition,
    AnyQueryDefinition,
    InferQueryDefinitionTagTypes,
} from './types';
import { refetchQueriesEvent } from './events';
import { createRequestScopedEvent } from './createRequestScopedEvent';

/**
 * Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
 *
 * @typeParam Definition - Описание эндпоинта
 */
export type EndpointQueryFlow<Definition extends AnyQueryDefinition> = {
    /**
     * Создает поток для запроса с указанными аргументами и опциями
     *
     * @param queryArgs - Аргументы запроса
     * @param options - Опции выполнения запроса
     * @returns Поток для работы с запросом
     */
    queryFlow: QueryFlowFactory<Definition>;
};

/**
 * Создает поток для запроса с указанными аргументами и опциями
 *
 * @typeParam Definition - Описание эндпоинта
 * @param queryArgs - Аргументы запроса
 * @param options - Опции выполнения запроса
 * @returns Поток для работы с запросом
 */
export type QueryFlowFactory<Definition extends AnyQueryDefinition> = (
    queryArgs: QueryArgFrom<Definition>,
    options?: QueryOptions
) => AsyncQueryFlow<Definition>;

/**
 * Параметры подписки на серверные данные
 */
export interface QueryOptions {
    /**
     * Автоматическое обновление раз в N ms
     * @default undefined
     */
    pollingInterval?: number;
    /**
     * Данная настройка позволяет отключать автоматическое обновление данных, если окно не в фокусе.
     * @default false
     */
    skipPollingIfUnfocused?: boolean;
    /**
     * Автоматическое обновление при фокусе во вкладку
     * @default false
     */
    refetchOnFocus?: boolean;
    /**
     * Автоматическое обновление при появлении сети
     * @default false
     */
    refetchOnReconnect?: boolean;
    /**
     * Нужно ли отправлять запрос сразу при создании потока, чтобы предзагрузить данные.
     * Если false, то запрос уйдет только при появлении первой подписки на поток.
     * @default true
     */
    prefetch?: boolean;
}

/**
 * Информация о текущем запросе к эндпоинту
 *
 * @typeParam Definition - Описание эндпоинта
 */
export interface QueryRequest<Definition extends AnyQueryDefinition> {
    /** ID последнего запроса */
    requestId: string;
    /** Время начала запроса в миллисекундах */
    startedTimestamp: number;
    /** Время окончания запроса в миллисекундах или null, если запрос еще не завершен */
    fulfilledTimestamp: number | null;
    /** Теги, которыми был инвалидирован последний запрос */
    invalidatedBy?: TagDescription<InferQueryDefinitionTagTypes<Definition>>[];
}

/**
 * Опции для метода flow.asPromise()
 */
export interface AsyncQueryFlowAsPromiseOptions {
    /** Сигнал для отмены запроса */
    signal?: AbortSignal;
}

/**
 * Опции для метода flow.refetch()
 */
export interface AsyncQueryFlowRefetchOptions {
    /**
     * Перезапросить только если данные старше N секунд
     */
    ifOlderThan?: number;
}

/**
 * Опции для метода flow.prefetch()
 */
export interface AsyncQueryFlowPrefetchOptions {
    /**
     * Флаг, который позволяет сделать запрос даже в том случае, если в кеше есть данные.
     */
    forceRefetch?: boolean;
    /**
     * Возраст данных в кеше (в секундах), после которого нужно перезапросить данные.
     * Если в кеше есть достаточно свежие данные, то метод вернет закешированный результат без запроса в бекенд.
     */
    refetchIfOlderThan?: number;
    /**
     * Сколько секунд удерживать предзагруженные данные живыми через подписку-держатель на поток,
     * пока на них не появится настоящая подписка. По истечении времени держатель снимается,
     * и данные будут удалены из кеша согласно настройке `keepUnusedDataFor` эндпоинта,
     * если на них больше никто не подписан.
     *
     * @default 60
     */
    keepPrefetchedDataFor?: number;
}

/**
 * Время удержания prefetch-подписки по умолчанию, в секундах.
 */
const DEFAULT_KEEP_PREFETCHED_DATA_FOR_SECONDS = 60;

/**
 * Внутренний символ для хранения onCacheHit внутри flow.
 */
const ON_CACHE_HIT = Symbol('queryFlow.onCacheHit');

/**
 * Поток метаданных текущего запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 */
type QueryRequestFlow<Definition extends AnyQueryDefinition> = Flow<QueryRequest<Definition> | null>;

/**
 * Расширенный тип потока для запросов.
 * Совместим со всем стандартным туллингом для обычного AsyncFlow.
 *
 * @typeParam Definition - Описание эндпоинта
 */
export interface AsyncQueryFlow<Definition extends AnyQueryDefinition> extends AsyncFlow<ResultTypeFrom<Definition>> {
    /** Имя эндпоинта, для которого создан поток */
    endpointName: string;
    /** Аргументы запроса, с которыми был создан поток */
    originalQueryArg: QueryArgFrom<Definition>;
    /** Информация о последнем выполненном запросе */
    currentRequest: QueryRequestFlow<Definition>;
    /** Перезапросить данные */
    refetch: (options?: AsyncQueryFlowRefetchOptions) => void;
    /** Отменить активный запрос, если есть */
    abort: () => void;
    /**
     * Расширенная функция asPromise, которая умеет принимать сигнал для отмены запроса,
     * а также умеет работать как data source для реакций.
     */
    asPromise: (
        options?: AsyncQueryFlowAsPromiseOptions
    ) => IterableDataSource<
        Promise<ResultTypeFrom<Definition>>,
        Promise<ResultTypeFrom<Definition>>,
        ResultTypeFrom<Definition>
    >;
    /**
     * Возвращает последнее материализованное состояние: без запуска запроса, без сверки
     * со стором и без уведомления слушателей. Состояние вправе быть устаревшим — актуальное
     * отдает getSnapshot().
     */
    getCachedSnapshot(): AsyncFlowState<ResultTypeFrom<Definition>>;
    /**
     * Предзагружает данные потока заранее (например, при переходе между страницами
     * или загрузке асинхронного чанка с кодом).
     *
     * В отличие от `subscribe()`, удерживает предзагруженные данные в кеше через
     * подписку-держатель на поток до тех пор, пока на них не появится настоящая подписка
     * (монтирование компонента-потребителя), либо пока не истечет `keepPrefetchedDataFor` —
     * что наступит раньше. Это гарантирует, что данные не будут удалены из кеша между
     * предзагрузкой и первым рендером потребителя — что особенно важно для эндпоинтов
     * с `keepUnusedDataFor: 0`, у которых иначе данные удаляются сразу после завершения запроса.
     *
     * Возвращаемый промис никогда не реджектится.
     */
    prefetch(options?: AsyncQueryFlowPrefetchOptions): Promise<void>;
}

/**
 * Параметры для создания фабрики потоков.
 */
type BuildQueryOptions = {
    /** Экземпляр API */
    api: AnyApi;
    /** Имя эндпоинта */
    endpointName: string;
    /** Описание эндпоинта */
    endpointDefinition: AnyEndpointDefinition;
    /** Функция сериализации аргументов запроса */
    serializeQueryArgs: InternalSerializeQueryArgs;
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Отключено ли кеширование для данного эндпоинта */
    isCacheDisabled: boolean;
};

/**
 * Создает функцию для построения потока запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param options - Опции для построения потока
 * @returns Функция для создания потока запроса
 */
export function buildEndpointQueryFlow<Definition extends AnyQueryDefinition>({
    api,
    endpointName,
    endpointDefinition,
    serializeQueryArgs,
    getStore,
    isCacheDisabled,
}: BuildQueryOptions): QueryFlowFactory<Definition> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- тип эндпоинта заранее не известен
    const endpoint = api.endpoints[endpointName] as ApiEndpointQuery<Definition, {}>;

    const buildCacheKey = createCacheKeyBuilder({ api, endpointName, endpointDefinition, serializeQueryArgs });

    const flowFactory = memoize(
        (param: { queryArgs: QueryArgFrom<Definition>; queryOptions: QueryOptions; cacheKey: string }) => {
            return createQueryFlow({
                endpoint,
                endpointName,
                queryArgs: param.queryArgs,
                options: param.queryOptions,
                getStore,
                isCacheDisabled,
            });
        },
        {
            equals(a, b) {
                return a.cacheKey === b.cacheKey;
            },
            onCacheHit(flow) {
                flow[ON_CACHE_HIT]();
            },
        }
    );

    function query(queryArgs: QueryArgFrom<Definition>, queryOptions?: QueryOptions): AsyncQueryFlow<Definition> {
        const shouldPrefetch = queryOptions?.prefetch ?? true;

        // Нормализуем типы в параметрах для эффективного кеширования инстансов
        const normalizedOptions: QueryOptions = {
            pollingInterval: queryOptions?.pollingInterval,
            skipPollingIfUnfocused: queryOptions?.skipPollingIfUnfocused ?? false,
            refetchOnFocus: queryOptions?.refetchOnFocus ?? false,
            refetchOnReconnect: queryOptions?.refetchOnReconnect ?? false,
        };

        const cacheKey = `${buildCacheKey(queryArgs)},${stableSerialize(normalizedOptions)}`;

        const flow = flowFactory({
            queryArgs,
            queryOptions: normalizedOptions,
            cacheKey,
        });

        if (shouldPrefetch) {
            flow.getSnapshot();
        }

        return flow;
    }

    return query;
}

/**
 * Опции для создания потока запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 */
interface CreateQueryFlowOptions<Definition extends AnyQueryDefinition> {
    /** Эндпоинт запроса */
    endpoint: ApiEndpointQuery<Definition, {}>;
    /** Имя эндпоинта */
    endpointName: string;
    /** Аргументы запроса */
    queryArgs: QueryArgFrom<Definition>;
    /** Опции запроса */
    options?: QueryOptions;
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Отключено ли кеширование для данного эндпоинта */
    isCacheDisabled: boolean;
}

/**
 * Внутренний тип flow, расширенный методом onCacheHit.
 */
type CachedQueryFlow<Definition extends AnyQueryDefinition> = AsyncQueryFlow<Definition> & {
    [ON_CACHE_HIT](): void;
};

/**
 * Держатель prefetch: подписка на поток, удерживающая данные в кеше, и таймер её снятия.
 */
interface PrefetchHolder {
    subscription: FlowSubscription;
    timer: ReturnType<typeof setTimeout>;
}

/**
 * Создает поток для отслеживания состояния запроса.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param options - Опции для создания потока
 * @returns Поток с прикрепленным onCacheHit
 */
function createQueryFlow<Definition extends AnyQueryDefinition>({
    endpoint,
    endpointName,
    queryArgs,
    options,
    getStore,
    isCacheDisabled,
}: CreateQueryFlowOptions<Definition>): CachedQueryFlow<Definition> {
    const dataSelector = endpoint.select(queryArgs);
    const getState = () => dataSelector(getStore().getState());

    // Ссылка на текущий prefetch-запрос (subscribe: false). Нужна, чтобы tryAbortQuery мог отменить его при отписке от потока.
    let prefetchQuery: QueryActionCreatorResult<Definition> | null = null;
    // Подписка на query в RTK Query (subscribe: true). Создаётся при вызове subscribe() на потоке.
    let querySubscription: QueryActionCreatorResult<Definition> | null = null;
    // Подписка на событие принудительного обновления запросов (refetchQueriesEvent).
    let refetchEventSubscription: (() => void) | null = null;
    // Держатели prefetch: каждый — обычная подписка на поток + таймаут снятия. Удерживают поток
    // активным (а значит и данные в кеше) до появления настоящей подписки либо истечения таймаута.
    const prefetchHolders = new Set<PrefetchHolder>();
    // Предыдущее состояние query из стора, используется для определения изменений.
    let prevState = getState();
    // Флаг необходимости вызвать dispatch(endpoint.initiate()) при вызове getSnapshot.
    // Dispatch нужен не столько для загрузки данных (RTKQ вернёт кеш если он есть),
    // сколько для продления таймера keepUnusedDataFor — без этого кеш может быть удалён
    // по старому таймеру.
    let needInitiate = true;

    const asyncFlow = createAsyncFlow<ResultTypeFrom<Definition>>(mapQueryState(prevState));
    const currentRequest = createCurrentRequestFlow(getStore, getState);
    const activeQueries = new Set<QueryActionCreatorResult<Definition>>();

    const storeListener = createStoreListener({
        getStore,
        getState,
        equals(a, b) {
            return a.status === b.status && a.data === b.data && a.error === b.error;
        },
        onChange(state) {
            try {
                asyncFlow.emit(mapQueryState(state));
            } catch (err) {
                // Метод emit() проксирует ошибки от слушателей потока.
                // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                // поэтому ловим ошибки и просто их логируем
                console.error(err);
            }
        },
    });

    const listeners = createListenersCollection({
        onAdd(listener) {
            return asyncFlow.subscribe(listener);
        },
        onActivate() {
            createSubscriptions();
            const unsubscribeReset = resetQueryFlows.subscribe(() => {
                // Сбрасываем prefetch-держатели без unsubscribe: listeners.reset() и так обнулит счетчик.
                discardPrefetchHolders();
                listeners.reset();
                flow.abort();
            });
            return () => {
                removeSubscriptions();
                unsubscribeReset();
            };
        },
    });

    /**
     * Подписывается на запрос в RTK Query для удержания данных в кеше.
     *
     * @returns Подписка на запрос
     */
    function subscribeToQuery() {
        const promise = getStore().dispatch(
            endpoint.initiate(queryArgs, {
                subscriptionOptions: {
                    pollingInterval: options?.pollingInterval,
                    skipPollingIfUnfocused: options?.skipPollingIfUnfocused,
                    refetchOnFocus: options?.refetchOnFocus,
                    refetchOnReconnect: options?.refetchOnReconnect,
                },
            })
        );
        trackQuery(promise);
        return promise;
    }

    /**
     * Подписывается на событие перезапроса данных.
     *
     * @returns Функция отписки от события
     */
    function subscribeToRefetchEvent() {
        return refetchQueriesEvent.subscribe(condition => {
            const queryState = getState();
            if (condition(queryState)) {
                flow.refetch();
            }
        });
    }

    /**
     * Создает все необходимые подписки для потока.
     */
    function createSubscriptions() {
        // Отменяем отложенный prefetch — subscribeToQuery сам инициирует запрос.
        // Используем unsubscribe, а не abort, чтобы не отменять уже летящий сетевой запрос.
        // Если setTimeout ещё не стрелял — отменяет таймер. Если уже стрелял — no-op
        // (prefetch создан с subscribe: false, отписываться не от чего).
        prefetchQuery?.unsubscribe();
        prefetchQuery = null;

        querySubscription = subscribeToQuery();
        refetchEventSubscription = subscribeToRefetchEvent();
        storeListener.subscribe();

        // Пока storeListener был неактивен, стор мог измениться (например, prefetch завершился).
        // syncState() синхронизирует asyncFlow с текущим состоянием стора, чтобы getSnapshot()
        // с подписчиками (который не вызывает syncState) возвращал актуальные данные.
        storeListener.syncState();
    }

    /**
     * Удаляет все подписки потока.
     */
    function removeSubscriptions() {
        querySubscription?.unsubscribe();
        querySubscription = null;
        refetchEventSubscription?.();
        refetchEventSubscription = null;
        storeListener.unsubscribe();
    }

    /**
     * Удаляет одну prefetch-подписку: ее таймаут и подписку на поток.
     */
    function releasePrefetchHolder(holder: PrefetchHolder) {
        if (!prefetchHolders.delete(holder)) {
            return;
        }
        clearTimeout(holder.timer);
        holder.subscription.unsubscribe();
    }

    /**
     * Снимает все prefetch-подписки. Вызывается при появлении настоящей подписки на поток.
     */
    function releaseAllPrefetchHolders() {
        for (const holder of [...prefetchHolders]) {
            releasePrefetchHolder(holder);
        }
    }

    /**
     * Сбрасывает все prefetch-подписки без unsubscribe (снимает только таймауты).
     * Используется, когда счетчик слушателей будет обнулен извне (resetQueryFlows).
     */
    function discardPrefetchHolders() {
        for (const holder of prefetchHolders) {
            clearTimeout(holder.timer);
        }
        prefetchHolders.clear();
    }

    /**
     * Отслеживает активный запрос для управления его жизненным циклом.
     *
     * @param query - Запрос, который нужно отслеживать
     */
    function trackQuery(query: QueryActionCreatorResult<Definition>) {
        activeQueries.add(query);

        const onEnd = () => activeQueries.delete(query);
        query.then(onEnd, onEnd);
    }

    /**
     * Пытается отменить запрос, если это возможно.
     *
     * @param currentQuery - Текущий запрос, который нужно отменить
     */
    function tryAbortQuery(currentQuery?: QueryActionCreatorResult<Definition>) {
        let canAbort = true;

        for (const queries of activeQueries) {
            // Отменять запрос можем только в том случае, если нет активных подписок, кроме
            // prefetch запроса и подписки для текущего запроса, который хотим отменить
            if (queries !== currentQuery && queries !== prefetchQuery) {
                canAbort = false;
                break;
            }
        }

        if (canAbort) {
            prefetchQuery?.abort();
            currentQuery?.abort();
        }
    }

    /**
     * Вызывается из memoize, когда queryFlow() возвращает уже существующий flow
     * (тот же cacheKey). Устанавливает needInitiate, чтобы при следующем getSnapshot был выполнен
     * dispatch endpoint.initiate(). Даже если данные есть в кеше RTKQ и новый сетевой запрос
     * не нужен, dispatch продлевает таймер keepUnusedDataFor — без этого кеш может быть
     * удалён по старому таймеру.
     */
    function onCacheHit() {
        needInitiate = true;
    }

    /**
     * Превращает следующее успешное значение потока в промис.
     *
     * @param options - Опции запроса
     */
    function asPromise(options?: AsyncQueryFlowAsPromiseOptions): Promise<ResultTypeFrom<Definition>> {
        if (options?.signal?.aborted) {
            return Promise.reject(options.signal.reason);
        }

        needInitiate = false;

        // Подписка триггерит запрос (если нужно), держит кеш до резолва промиса, обновляет таймеры для очистки кеша.
        const promiseQuerySubscription = getStore().dispatch(endpoint.initiate(queryArgs));
        trackQuery(promiseQuerySubscription);

        if (!listeners.hasListeners()) {
            storeListener.syncState();
        }

        // Отслеживаем изменения в сторе, чтобы зарезолвить промис.
        let syncQueued = false;
        const promiseStoreSubscription = getStore().subscribe(() => {
            // Используем микротаск для объединения нескольких синхронных dispatch в одно обновление.
            // Это предотвращает преждевременный resolve:
            // RTK Query с invalidationBehavior: "delayed" откладывает инвалидацию до завершения pending-запросов.
            // Когда запрос завершается, store обновляется до fulfilled, а затем middleware синхронно запускает re-fetch
            // из-за отложенной инвалидации.
            // Без батчинга asyncFlow зарезолвился бы с промежуточными данными первого запроса,
            // не дождавшись актуальных данных после re-fetch.
            if (!syncQueued) {
                syncQueued = true;

                // Привязываем syncState к жизненному циклу запроса, а не к Promise.resolve().
                // promiseQuerySubscription резолвится через thunkResult,
                // который завершается после обработки fulfilled action всеми middleware,
                // включая lifecycle-хэндлеры (onQueryStarted/queryFulfilled).
                // Это гарантирует, что побочные эффекты из onQueryStarted (например updateQueryData)
                // выполнятся до того, как asyncFlow.asPromise() зарезолвится.
                promiseQuerySubscription.then(
                    () => {
                        syncQueued = false;
                        storeListener.syncState();
                    },
                    () => {
                        syncQueued = false;
                        storeListener.syncState();
                    }
                );
            }
        });

        const onEnd = () => {
            promiseQuerySubscription.unsubscribe();
            promiseStoreSubscription();
        };
        asyncFlow.asPromise().then(onEnd, onEnd);

        options?.signal?.addEventListener('abort', () => {
            tryAbortQuery(promiseQuerySubscription);
        });

        return asyncFlow.asPromise();
    }

    const flow: CachedQueryFlow<Definition> = {
        endpointName,
        originalQueryArg: queryArgs,
        currentRequest,
        [ON_CACHE_HIT]: onCacheHit,
        subscribe(listener) {
            const subscription = listeners.add(listener);
            releaseAllPrefetchHolders();
            return subscription;
        },
        prefetch(options) {
            if (options?.forceRefetch) {
                flow.refetch();
            } else if (options?.refetchIfOlderThan != null) {
                flow.refetch({ ifOlderThan: options.refetchIfOlderThan });
            }

            // Держатель нужен только на клиенте. На сервере данные и так не удаляются.
            if (typeof window !== 'undefined') {
                const keepPrefetchedDataForMs =
                    (options?.keepPrefetchedDataFor ?? DEFAULT_KEEP_PREFETCHED_DATA_FOR_SECONDS) * 1000;
                const holder: PrefetchHolder = {
                    subscription: listeners.add(() => {}),
                    timer: setTimeout(() => releasePrefetchHolder(holder), keepPrefetchedDataForMs),
                };
                prefetchHolders.add(holder);
            }

            // Запускаем запрос (если нужно) и дожидаемся загрузки. Промис не реджектится:
            // ошибки предзагрузки не должны ломать вызывающий код, данные подтянутся при подписке.
            return asPromise().then(
                () => undefined,
                () => undefined
            );
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                const state = getState();

                // Проверка !prefetchQuery предотвращает повторный dispatch при множественных
                // вызовах getSnapshot (React StrictMode, onCacheHit).
                if ((state.isUninitialized || needInitiate) && !prefetchQuery) {
                    needInitiate = false;
                    // При отключённом кеше намеренно не инициируем запрос, т.к. данные
                    // были бы сразу удалены из стора. Загрузка данных в этом случае происходит
                    // через subscribe() или asPromise().
                    if (!isCacheDisabled) {
                        const query = getStore().dispatch(endpoint.initiate(queryArgs, { subscribe: false }));
                        prefetchQuery = query;
                        trackQuery(query);
                        const onEnd = () => {
                            prefetchQuery = null;
                        };
                        query.then(onEnd, onEnd);
                    }
                }

                storeListener.syncState();
            }

            return asyncFlow.getSnapshot();
        },
        asPromise: wrapDataSource(
            asPromise,
            listener => listeners.add(listener),
            () => asyncFlow.getSnapshot()
        ),
        refetch(refetchOptions) {
            const promise = getStore().dispatch(
                endpoint.initiate(queryArgs, {
                    forceRefetch: refetchOptions?.ifOlderThan ?? true,
                    subscribe: false,
                })
            );
            trackQuery(promise);
        },
        abort() {
            for (const query of activeQueries) {
                query.abort();
            }
        },
        getCachedSnapshot() {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- поток создан через createAsyncFlow с начальным состоянием, кеш непустой с момента создания
            return asyncFlow.getCachedSnapshot()!;
        },
    };

    return flow;
}

/**
 * Преобразует состояние запроса из RTK Query в состояние AsyncFlow.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param state - Состояние запроса из RTK Query
 * @returns Состояние AsyncFlow
 */
function mapQueryState<Definition extends AnyQueryDefinition>(
    state: QueryResultSelectorResult<Definition>
): AsyncFlowState<ResultTypeFrom<Definition>> {
    if (state.status === 'fulfilled') {
        return { status: 'success', data: state.data };
    }

    if (state.status === 'rejected') {
        return { status: 'error', data: state.data, error: state.error };
    }

    return { status: 'pending', data: state.data };
}

/**
 * Создает поток для отслеживания информации о текущем запросе.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param getStore - Функция для получения store
 * @param getState - Функция для получения состояния запроса
 * @returns Поток с информацией о текущем запросе
 */
function createCurrentRequestFlow<Definition extends AnyQueryDefinition>(
    getStore: GetStoreFn,
    getState: () => QueryResultSelectorResult<Definition> & AdditionalQueryState<Definition>
): QueryRequestFlow<Definition> {
    const flow = createFlow<QueryRequest<Definition> | null>(mapQueryRequestState(getState()));

    const storeListener = createStoreListener({
        getStore,
        getState,
        equals(a, b) {
            return (
                a.requestId === b.requestId &&
                a.startedTimeStamp === b.startedTimeStamp &&
                a.fulfilledTimeStamp === b.fulfilledTimeStamp &&
                a.invalidatedBy === b.invalidatedBy
            );
        },
        onChange(state) {
            try {
                flow.emit(mapQueryRequestState(state));
            } catch (err) {
                // Метод emit() проксирует ошибки от слушателей потока.
                // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                // поэтому ловим ошибки и просто их логируем
                console.error(err);
            }
        },
    });

    const listeners = createListenersCollection({
        onAdd(listener) {
            return flow.subscribe(listener);
        },
        onActivate() {
            storeListener.subscribe();
            storeListener.syncState();
            const unsubscribeReset = resetQueryFlows.subscribe(() => {
                listeners.reset();
            });
            return () => {
                storeListener.unsubscribe();
                unsubscribeReset();
            };
        },
    });

    const currentRequestFlow: Flow<QueryRequest<Definition> | null> = {
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                storeListener.syncState();
            }

            return flow.getSnapshot();
        },
        getCachedSnapshot() {
            return flow.getCachedSnapshot();
        },
    };

    return currentRequestFlow;
}

/**
 * Преобразует состояние запроса в информацию о запросе.
 *
 * @typeParam Definition - Описание эндпоинта
 * @param state - Состояние запроса из RTK Query
 * @returns Информация о запросе или null, если запрос не был начат
 */
function mapQueryRequestState<Definition extends AnyQueryDefinition>(
    state: QueryResultSelectorResult<Definition> & AdditionalQueryState<Definition>
): QueryRequest<Definition> | null {
    if (!state.requestId) {
        return null;
    }

    return {
        requestId: state.requestId,
        startedTimestamp: state.startedTimeStamp,
        fulfilledTimestamp: state.fulfilledTimeStamp ?? null,
        invalidatedBy: state.invalidatedBy,
    };
}

/**
 * Опции для создания слушателя изменений в store.
 *
 * @template T - Тип состояния
 */
interface StoreListenerOptions<T> {
    /** Функция для получения store */
    getStore: GetStoreFn;
    /** Функция для получения состояния */
    getState: () => T;
    /** Функция сравнения состояний */
    equals: (a: T, b: T) => boolean;
    /** Callback при изменении состояния */
    onChange: (state: T) => void;
}

/**
 * Создает слушатель изменений в store.
 *
 * @template T - Тип состояния
 * @param options - Опции слушателя
 * @returns Объект с методами управления подпиской
 */
export function createStoreListener<T>({ getStore, getState, equals, onChange }: StoreListenerOptions<T>) {
    let prevState = getState();
    let storeSubscription: (() => void) | null = null;

    function syncState() {
        const currentState = getState();

        if (!equals(currentState, prevState)) {
            prevState = currentState;
            onChange(currentState);
        }
    }

    return {
        syncState,
        subscribe() {
            storeSubscription?.();
            storeSubscription = getStore().subscribe(syncState);
        },
        unsubscribe() {
            storeSubscription?.();
            storeSubscription = null;
        },
    };
}

/**
 * Ссылка на объект, в который нужно записать значение промиса для последующего синхронного использования.
 * @typeParam T - Тип данных, возвращаемых промисом
 */
type PromiseValueRef<T> = {
    /* Значение промиса */
    value: T;
    /* Признак того, что промис был успешно разрешен */
    fulfilled: boolean;
};

/**
 * Оборачивает промис-функцию через createDataSource, чтобы результат был полноценным data source для реакций.
 *
 * @typeParam T - Тип данных, возвращаемых промисом
 * @param getPromise - Функция, возвращающая промис с данными запроса
 * @param subscribe - Функция подписки на изменения данных потока
 * @param getSnapshot - Функция для получения текущего состояния потока
 * @deprecated Нужно будет удалить после того, как реакции научатся работать с потоками напрямую
 */
function wrapDataSource<T>(
    getPromise: (options?: AsyncQueryFlowAsPromiseOptions) => Promise<T>,
    subscribe: (listener: () => void) => { unsubscribe: () => void },
    getSnapshot: () => AsyncFlowState<T>
) {
    return createDataSource({
        getValue(options?: AsyncQueryFlowAsPromiseOptions) {
            const rawPromise = getPromise(options);

            // Сохраняем результат выполнения промиса в ссылку, чтобы иметь к нему синхронный доступ внутри итератора.
            // Побочный .then() не оборачивает промис — возвращается оригинальный rawPromise, чтобы сохранить идентичность.
            const valueRef: PromiseValueRef<unknown> = { value: undefined, fulfilled: false };
            rawPromise.then(
                data => {
                    valueRef.fulfilled = true;
                    valueRef.value = data;
                },
                () => {
                    // Ошибка обрабатывается через yield в реакции, здесь только подавляем unhandled rejection
                }
            );

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.assign не позволяет написать без тайпкаста
            return Object.assign(rawPromise, {
                [Symbol.iterator]: function* () {
                    yield rawPromise;

                    /**
                     * Когда вызывается `yield rawPromise` управление передается в реакцию.
                     * Там есть await rawPromise.
                     * Если rawPromise выбросит ошибку, то она будет передана в *data, а сюда выполнение не попадет.
                     * Поэтому всегда ожидаем, что промис к этому моменту уже разрешен.
                     * Побочный .then() зарегистрирован раньше, чем реакция подпишется на промис,
                     * поэтому valueRef гарантированно заполнен к моменту возобновления итератора.
                     */
                    if (!valueRef.fulfilled) {
                        throw new UnexpectedError('Query promise has not been resolved', { cause: null });
                    }

                    return valueRef.value;
                },
            }) as unknown as IterableDataSource<Promise<T>, Promise<T>, T>;
        },
        createSubscribe(waitQueryPromise) {
            return notify => {
                // Подписка создается до получения ответа. При получении ответа данные в сторе меняются,
                // что приводит к оповещению подписчиков стора.
                // Поэтому, игнорируем оповещения до разрешения waitQueryPromise
                let isInitiated = false;

                // Записываем в prevValue текущие данные,
                // чтобы избежать дублирования запусков с теми же данными
                let prevData: unknown;

                if (waitQueryPromise instanceof Promise) {
                    waitQueryPromise
                        .then(data => {
                            prevData = data;
                        })
                        .catch(err => {
                            prevData = err;
                        })
                        .finally(() => {
                            isInitiated = true;
                        });
                } else {
                    console.error(
                        new UnexpectedError('Неожиданный тип waitQueryPromise', {
                            cause: undefined,
                            additional: { type: typeof waitQueryPromise },
                        })
                    );
                }

                const subscription = subscribe(() => {
                    const state = getSnapshot();

                    // Игнорируем все изменения, кроме завершения запроса
                    if (!isInitiated || (state.status !== 'success' && state.status !== 'error')) {
                        return;
                    }

                    const dataOrError = state.status === 'success' ? state.data : state.error;

                    // queryFlow не гарантирует, что data изменилась между вызовами подписчика.
                    // RTK Query использует structuralSharing:
                    // при re-fetch с тем же результатом copyWithStructuralSharing
                    // сохраняет старую ссылку на data. При этом status меняется
                    // (pending → fulfilled), что вызывает storeListener.onChange
                    // и asyncFlow.emit. Без этой проверки реакция перезапустится
                    // вхолостую с теми же данными.
                    if (prevData !== dataOrError) {
                        prevData = dataOrError;
                        notify();
                    }
                });

                return () => {
                    subscription.unsubscribe();
                };
            };
        },
    });
}

/**
 * Событие, которое позволяет удалить подписки для всех потоков.
 * Используется для очистки состояния между тестами.
 *
 * Подписки скоупятся по контексту (на сервере — по SSR-запросу), поэтому обработчики
 * не удерживают потоки и сторы завершенных запросов в памяти процесса. Как следствие,
 * publish() сбрасывает только подписки текущего контекста: подписки завершенного
 * SSR-запроса сбрасывать не нужно — они освобождаются вместе с самим запросом.
 * @internal
 */
export const resetQueryFlows = createRequestScopedEvent('resetQueryFlows');

/**
 * Создает функцию вычисления ключа кеширования потоков из параметров эндпоинта.
 */
function createCacheKeyBuilder({
    api,
    endpointName,
    endpointDefinition,
    serializeQueryArgs,
}: {
    api: AnyApi;
    endpointName: string;
    endpointDefinition: AnyEndpointDefinition;
    serializeQueryArgs: InternalSerializeQueryArgs;
}) {
    return (queryArgs: QueryArgFrom<AnyQueryDefinition>) => {
        const queryCacheKey = serializeQueryArgs({ endpointDefinition, endpointName, queryArgs });
        return `${api.reducerPath}/${queryCacheKey}`;
    };
}
